import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {getInternalClubId} from "@/utils/constants";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RateReviewIcon from "@mui/icons-material/RateReview";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {MatchAPI} from "@/api/match";
import VLFTextField from "@/components/Input/TextField";
import {
	getPipelineStage,
	useScoutingPipeline,
	toggleMonitored,
} from "@/utils/scoutingPipeline";
import {ATTITUDES} from "@/utils/scoutingData";
import {isEmptyRow, toggleAttitude} from "@/utils/scoutingVisions";
import {reportMatch} from "@/utils/scoutingReports";

import {AREA_OPTIONS} from "@/utils/constants";

import "./RostersSection.scss";

const orderMap = AREA_OPTIONS.reduce((acc, role) => {
	acc[role.label] = role.order;
	return acc;
}, {});

const sortByRole = (a, b) =>
	(orderMap[a.role] ?? 99) - (orderMap[b.role] ?? 99);

const matchPlayerName = (p) =>
	p.playerName ||
	(p.player
		? `${p.player.firstName || ""} ${p.player.lastName || ""}`.trim()
		: "") ||
	"Nome non indicato";

// I giocatori gia' registrati sulla partita: il lato e' il discriminante,
// con fallback sul club quando non e' valorizzato.
const matchPlayersOfSide = (matchData, side, clubId) =>
	(matchData?.matchPlayers ?? []).filter((p) =>
		p.matchSide
			? p.matchSide === side
			: p.clubId === clubId && matchData?.homeClubId !== matchData?.awayClubId,
	);

// Unisce la rosa della squadra con chi e' gia' in distinta, senza duplicati.
const mergeRoster = (rosterPlayers, matchPlayers) => {
	const rows = (rosterPlayers || []).map((p) => ({
		key: "roster_" + p.playerId,
		playerId: p.playerId,
		name: p.playerName,
		number: p.playerNumber,
		role: p.role,
		year: p.playerYear,
		participationId: null,
	}));

	(matchPlayers || []).forEach((p) => {
		const existing = p.playerId
			? rows.find((r) => String(r.playerId) === String(p.playerId))
			: null;

		// Chi e' in distinta porta l'id della partecipazione: serve per rimuoverlo.
		if (existing) {
			existing.participationId = p.id;
			existing.number = p.playerNumber ?? existing.number;
			return;
		}

		rows.push({
			key: "match_" + p.id,
			playerId: p.playerId,
			name: matchPlayerName(p),
			number: p.playerNumber,
			role: p.roleSnapshot || p.player?.primaryRole || "",
			year: p.playerYear,
			participationId: p.id,
		});
	});

	return rows.sort(sortByRole);
};

const searchKey = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

const STARTER_OPTIONS = [
	[true, "Tit", "Titolare"],
	[false, "Ris", "Riserva"],
];

// Annotazioni della visione: titolare o riserva, attitudini, nota.
export function VisionControls({row, name, onChange}) {
	const starter = row?.starter ?? null;
	const attitudes = row?.attitudes || [];
	return (
		<div className="sr-vision">
			<div className="sr-vision__controls">
				<div
					className="sr-vision__group"
					role="group"
					aria-label={"Titolare o riserva " + name}
				>
					{STARTER_OPTIONS.map(([value, short, label]) => (
						<button
							key={short}
							type="button"
							title={label}
							aria-pressed={starter === value}
							className={
								"sr-vision__chip" + (starter === value ? " is-active" : "")
							}
							onClick={() =>
								onChange({starter: starter === value ? null : value})
							}
						>
							{short}
						</button>
					))}
				</div>
				<div
					className="sr-vision__group"
					role="group"
					aria-label={"Attitudini " + name}
				>
					{ATTITUDES.map((a) => {
						const active = attitudes.includes(a.id);
						return (
							<button
								key={a.id}
								type="button"
								title={a.label}
								aria-pressed={active}
								className={
									"sr-vision__chip sr-vision__chip--attitude" +
									(active ? " is-active" : "")
								}
								style={{"--attitude-color": a.color}}
								onClick={() =>
									onChange({attitudes: toggleAttitude(attitudes, a.id)})
								}
							>
								{a.id}
							</button>
						);
					})}
				</div>
			</div>
			<div className="sr-vision__note">
				<VLFTextField
					multiline
					minRows={2}
					maxRows={8}
					size="small"
					placeholder="Nota"
					inputProps={{maxLength: 2000, "aria-label": "Nota " + name}}
					value={row?.note || ""}
					onChange={(e) => onChange({note: e.target.value})}
				/>
			</div>
		</div>
	);
}

// Riepilogo delle annotazioni quando la riga è chiusa.
const visionSummary = (row) =>
	isEmptyRow(row)
		? null
		: [
				STARTER_OPTIONS.find(([value]) => value === row.starter)?.[2],
				(row.attitudes || []).join(" "),
				row.note?.trim() ? "nota" : null,
			]
				.filter(Boolean)
				.join(" · ");

function PlayerRow({
	row,
	onDeletePlayer,
	onObservePlayer,
	onOpenPlayer,
	targetIds = [],
	isInternal,
	pipelineEntry,
	onToggleMonitored,
	visionRow,
	onVisionChange,
	match,
}) {
	const [annotating, setAnnotating] = useState(false);
	const stage = getPipelineStage(pipelineEntry?.status);
	const lockedStage = stage && stage.id !== "watching" ? stage : null;
	const canAnnotate = !!(onVisionChange && row.playerId);
	const summary = canAnnotate && !annotating && visionSummary(visionRow);
	return (
		<div className="sr-player-row">
			<span className="sr-jersey">{row.number ?? "–"}</span>
			<div className="sr-player-info">
				{row.playerId && onOpenPlayer ? (
					<button
						type="button"
						className="sr-player-name sr-player-link"
						title="Apri scheda giocatore"
						aria-label={"Apri scheda " + row.name}
						onClick={() => onOpenPlayer(row.playerId)}
					>
						{row.name}
					</button>
				) : (
					<span className="sr-player-name">{row.name}</span>
				)}
				<span className="sr-player-meta">
					{[row.role, row.year].filter(Boolean).join(" · ")}
				</span>
				{summary && <span className="sr-player-summary">{summary}</span>}
			</div>
			{canAnnotate && (
				<Tooltip title={annotating ? "Chiudi" : "Annota"}>
					<button
						type="button"
						className={
							"sr-icon-btn sr-annotate-btn" +
							(annotating || summary ? " is-active" : "")
						}
						aria-label={"Annota " + row.name}
						aria-expanded={annotating}
						onClick={() => setAnnotating((open) => !open)}
					>
						<EditNoteIcon sx={{fontSize: 20}} />
					</button>
				</Tooltip>
			)}
			{onToggleMonitored && row.playerId && !isInternal && (
				<Tooltip
					title={
						lockedStage
							? "In pipeline: " + lockedStage.label
							: stage
								? "Togli da monitorati"
								: "Da monitorare"
					}
				>
					<span>
						<button
							type="button"
							className={
								"sr-icon-btn sr-star-btn" + (stage ? " is-active" : "")
							}
							aria-label={(stage ? "Monitorato " : "Da monitorare ") + row.name}
							aria-pressed={!!stage}
							disabled={!!lockedStage}
							onClick={() => onToggleMonitored(row.playerId)}
						>
							{stage ? (
								<StarIcon sx={{fontSize: 18}} />
							) : (
								<StarBorderIcon sx={{fontSize: 18}} />
							)}
						</button>
					</span>
				</Tooltip>
			)}
			{row.playerId && (
				<Tooltip title={isInternal ? "Nuova valutazione" : "Nuova relazione"}>
					<Link
						className="sr-icon-btn sr-report-btn"
						aria-label={
							(isInternal ? "Nuova valutazione " : "Nuova relazione ") +
							row.name
						}
						to={
							(isInternal
								? "/players-valuations/new?playerId="
								: "/scouting/relazioni/new?playerId=") +
							encodeURIComponent(row.playerId)
						}
						state={isInternal ? undefined : {match: reportMatch(match)}}
					>
						<RateReviewIcon sx={{fontSize: 18}} />
					</Link>
				</Tooltip>
			)}
			{onObservePlayer && row.playerId && (
				<Tooltip
					title={
						targetIds.includes(row.playerId)
							? "Gia' tra gli obiettivi"
							: "Aggiungi agli obiettivi scouting"
					}
				>
					<span>
						<button
							className="sr-icon-btn"
							type="button"
							aria-label={`Osserva ${row.name}`}
							disabled={targetIds.includes(row.playerId)}
							onClick={() => onObservePlayer(row.playerId).catch(() => {})}
						>
							<VisibilityOutlinedIcon fontSize="small" />
						</button>
					</span>
				</Tooltip>
			)}
			{onDeletePlayer && row.participationId && (
				<Tooltip title="Rimuovi dalla partita">
					<button
						type="button"
						className="sr-icon-btn sr-icon-btn--delete"
						aria-label={`Rimuovi ${row.name}`}
						onClick={() => onDeletePlayer(row)}
					>
						<CloseIcon sx={{fontSize: 15}} />
					</button>
				</Tooltip>
			)}
			{canAnnotate && annotating && (
				<VisionControls
					row={visionRow}
					name={row.name}
					onChange={(patch) => onVisionChange(row.playerId, patch)}
				/>
			)}
		</div>
	);
}

function SideRoster({
	matchData,
	col,
	onAddPlayer,
	onDeletePlayer,
	onObservePlayer,
	onOpenPlayer,
	onRosterLoaded,
	targetIds,
	visionRows,
	onVisionChange,
	query,
	view,
}) {
	const [roster, setRoster] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [reload, setReload] = useState(0);
	const {
		pipeline,
		canWrite: canManagePipeline,
		error: pipelineError,
	} = useScoutingPipeline();
	const [monitorError, setMonitorError] = useState(null);

	useEffect(() => {
		if (!matchData?.id) {
			return;
		}

		let active = true;
		setLoading(true);
		setError("");

		MatchAPI.getLineupRoster(matchData.id, col.side)
			.then((data) => {
				if (!active) return;
				setRoster(data);
				onRosterLoaded?.(col.side, data);
			})
			.catch((err) => {
				if (active) {
					setRoster(null);
					onRosterLoaded?.(col.side, null);
					setError(
						err?.response?.data?.error || "Impossibile caricare la rosa.",
					);
				}
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => {
			active = false;
		};
	}, [matchData?.id, col.side, reload]); // eslint-disable-line react-hooks/exhaustive-deps

	const inMatch = matchPlayersOfSide(matchData, col.side, col.clubId);
	const rows = mergeRoster(roster?.players, inMatch);
	const key = searchKey(query).trim();
	const visible = rows.filter(
		(r) =>
			(!key || searchKey(r.name).includes(key)) &&
			(view === "called"
				? !!r.participationId
				: view === "annotated"
					? !isEmptyRow(visionRows?.[r.playerId])
					: true),
	);

	const handleToggleMonitored = async (playerId) => {
		try {
			await toggleMonitored(playerId);
			setMonitorError(null);
		} catch (error) {
			setMonitorError(
				error.response?.data?.error ||
					error.message ||
					"Monitoraggio non salvato",
			);
		}
	};

	return (
		<div className="sr-col">
			<div className="sr-col__header">
				<div className="sr-col__titles">
					<span className="sr-col__side">{col.label}</span>
					<span className="sr-col__name">
						{col.club || "Squadra da inserire"}
					</span>
				</div>
				{visible.length > 0 && (
					<span className="sr-col__count">{visible.length}</span>
				)}
				<Tooltip title="Ricarica rosa">
					<button
						type="button"
						className="sr-icon-btn"
						aria-label={`Ricarica rosa ${col.label}`}
						onClick={() => setReload((n) => n + 1)}
					>
						<RefreshIcon sx={{fontSize: 17}} />
					</button>
				</Tooltip>
				{onAddPlayer && (
					<Tooltip title="Aggiungi giocatore alla partita">
						<button
							type="button"
							className="sr-icon-btn"
							aria-label={`Aggiungi giocatore ${col.label}`}
							onClick={() => onAddPlayer(col.side, col.clubId)}
						>
							<AddIcon sx={{fontSize: 19}} />
						</button>
					</Tooltip>
				)}
			</div>
			{roster?.rosterName && (
				<div className="sr-col__roster">Dalla rosa {roster.rosterName}</div>
			)}
			<div className="sr-col__body">
				{(pipelineError || monitorError) && (
					<div role="alert" className="sr-empty sr-empty--error">
						{monitorError ||
							"Monitoraggio non disponibile o accesso non consentito."}
					</div>
				)}
				{loading ? (
					<div className="sr-empty" role="status">
						Caricamento rosa...
					</div>
				) : error ? (
					<div className="sr-empty sr-empty--error" role="alert">
						{error}
					</div>
				) : visible.length ? (
					visible.map((row) => (
						<PlayerRow
							key={row.key}
							row={row}
							match={matchData}
							onDeletePlayer={onDeletePlayer}
							onObservePlayer={
								onObservePlayer && ((id) => onObservePlayer(id, col.side))
							}
							onOpenPlayer={onOpenPlayer}
							targetIds={targetIds}
							isInternal={col.clubId === getInternalClubId()}
							pipelineEntry={pipeline[row.playerId]}
							onToggleMonitored={
								canManagePipeline ? handleToggleMonitored : undefined
							}
							visionRow={visionRows?.[row.playerId]}
							onVisionChange={
								onVisionChange &&
								((playerId, patch) => onVisionChange(playerId, col.side, patch))
							}
						/>
					))
				) : rows.length ? (
					<div className="sr-empty">Nessun giocatore per questa ricerca.</div>
				) : (
					<div className="sr-empty">
						{roster?.rosterId
							? "La rosa non contiene giocatori. Aggiungili per valutarli."
							: "Nessuna rosa associata a questa squadra. Aggiungi i giocatori da valutare."}
					</div>
				)}
			</div>
		</div>
	);
}

export default function RostersSection({
	matchData,
	onAddPlayer,
	onDeletePlayer,
	onObservePlayer,
	onOpenPlayer,
	onRosterLoaded,
	targetIds,
	visionRows,
	onVisionChange,
	query,
	view,
}) {
	const sides = [
		{
			side: "home",
			label: "Casa",
			club: matchData?.homeClub,
			clubId: matchData?.homeClubId,
		},
		{
			side: "away",
			label: "Trasferta",
			club: matchData?.awayClub,
			clubId: matchData?.awayClubId,
		},
	];

	return (
		<div className="sr-wrap">
			{sides.map((col) => (
				<SideRoster
					key={col.side}
					matchData={matchData}
					col={col}
					onAddPlayer={onAddPlayer}
					onDeletePlayer={onDeletePlayer}
					onObservePlayer={onObservePlayer}
					onOpenPlayer={onOpenPlayer}
					onRosterLoaded={onRosterLoaded}
					targetIds={targetIds}
					visionRows={visionRows}
					onVisionChange={onVisionChange}
					query={query}
					view={view}
				/>
			))}
		</div>
	);
}
